import type { NewPaymentPayload } from '@api/hooks/types/createTransaction';
import type { Account } from '@api/hooks/useAccounts.ts';
import { useCreateTransaction } from '@api/hooks/useTransactions';
import { ControlledAccountList } from '@components/AccountList';
import Alert from '@components/alert';
import { DocumentNumberInput } from '@components/DocumentNumberInput';
import { FormBlocker } from '@components/FormBlocker';
import { PermissionGate } from '@components/PermissionGate';
import { PaymentTfa, SigningStatus } from '@components/tfa/PaymentTfa.tsx';
import { Button, DateStringPicker, TextArea } from '@gurupay/gp-ui-library';
import { addYears, startOfDay } from 'date-fns';
import { enUS, lt } from 'date-fns/locale';
import type { FC } from 'react';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { FXCurrencyFormConfigEntry, FXPayment } from 'types/operations';
import { Capability } from 'types/roles';

import { FormBuilder, TransferInput } from './components';
import { PaymentTypeSwitcher } from './components/PaymentTypeSwitcher';
import { useBicValidationContext } from './contexts/BicValidationContext.tsx';
import type { FXFormValues } from './hooks/useFXFormState';
import { useFXFormState } from './hooks/useFXFormState';
import { useOnCurrencyChange } from './hooks/useOnCurrencyChange';

export type { FXFormValues };

enum FormStatus {
	INITIAL = 'initial',
	CREATED = 'created',
}

// The currency-change clearing in useFXFormState resets every beneficiary leaf
// to '', including ones the selected corridor never renders (e.g. USD/SWIFT has
// no bank address town/postal_code/building_number inputs). The backend converts
// '' to null and rejects it with "must be a string", so empty leaves (and objects
// left with no data, e.g. an address holding only its constant `type`) must be
// stripped from the payload.
const pruneEmptyValues = (
	obj: Record<string, unknown>,
	key?: string,
): Record<string, unknown> | undefined => {
	const result: Record<string, unknown> = {};
	Object.entries(obj).forEach(([childKey, value]) => {
		if (value && typeof value === 'object' && !Array.isArray(value)) {
			const nested = pruneEmptyValues(value as Record<string, unknown>, childKey);
			if (nested) {
				result[childKey] = nested;
			}
			return;
		}
		if (value !== '' && value !== undefined && value !== null) {
			result[childKey] = value;
		}
	});
	const keys = Object.keys(result);
	if (keys.length === 0 || (keys.length === 1 && keys[0] === 'type')) {
		return undefined;
	}
	// A surviving address must carry a discriminator (the backend rejects one that
	// is present but missing `type`). The classifier-declared type (set via the
	// hidden `.type` field) wins; only fall back to STRUCTURED when it is absent —
	// e.g. a manually filled address on a corridor with no `.type` config.
	if (key === 'address' && result.type === undefined) {
		result.type = 'STRUCTURED';
	}
	return result;
};

export interface FXFormContentProps {
	operation?: FXPayment;
	selectedAccount?: Account;
	classifiers: FXCurrencyFormConfigEntry[];
}

export const FXFormContent: FC<FXFormContentProps> = ({
	operation,
	selectedAccount,
	classifiers,
}) => {
	const { t, i18n } = useTranslation();
	const { resetAll: resetBicValidation } = useBicValidationContext() ?? {};
	const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.INITIAL);
	const [signingStatus, setSigningStatus] = useState<SigningStatus | undefined>();
	const {
		data: createdTransaction,
		mutateAsync: createTransaction,
		isError: failedToCreateTransaction,
		isPending,
	} = useCreateTransaction();

	const { fxTransferFrom, isoCodes, fxCurrency, accountUuid, hasBoth, allFields } =
		useFXFormState({
			classifiers,
			operation,
		});

	// Reset BIC validation when the user switches corridor.
	useOnCurrencyChange(fxCurrency, () => resetBicValidation?.());

	const fxQuote = fxTransferFrom.watch('fx_quote');

	const setDefaultValue = (newAccountUuid: string) => {
		fxTransferFrom.setValue('account_uuid', newAccountUuid);
	};

	const onSubmit: SubmitHandler<FXFormValues> = (data) => {
		if (!data.fx_currency) {
			return;
		}

		const payload = {
			...data,
			beneficiary: data.beneficiary
				? (pruneEmptyValues(
						data.beneficiary as Record<string, unknown>,
					) as FXFormValues['beneficiary'])
				: undefined,
		};

		void createTransaction(
			{ data: payload as unknown as NewPaymentPayload },
			{
				onSuccess: () => {
					setFormStatus(FormStatus.CREATED);
				},
			},
		);
	};

	const handleSignTransaction = (status: SigningStatus) => {
		fxTransferFrom.reset(undefined, { keepDefaultValues: true, keepDirty: false });
		resetBicValidation?.();
		setFormStatus(FormStatus.INITIAL);
		setSigningStatus(status);
	};

	const isDisabled = formStatus === FormStatus.CREATED;

	return (
		<FormProvider {...fxTransferFrom}>
			<form onSubmit={fxTransferFrom.handleSubmit(onSubmit)}>
				<ControlledAccountList
					defaultValue={selectedAccount}
					isDisabled={isDisabled}
					label={t('payments.common.from')}
					name="account_uuid"
					setDefaultValue={selectedAccount?.uuid ? undefined : setDefaultValue}
				/>
				{Object.keys(fxTransferFrom.formState.touchedFields).length === 0 && (
					<div className="mt-space-16">
						{signingStatus === SigningStatus.ERROR && (
							<Alert type={'error'}>{t('payments.common.orderSigningFailed')}</Alert>
						)}
						{signingStatus === SigningStatus.SUCCESS && (
							<Alert type={'success'}>{t('payments.common.orderSigned')}</Alert>
						)}
						{signingStatus === SigningStatus.CANCELED && (
							<Alert type={'warning'}>{t('payments.common.orderSigningCanceled')}</Alert>
						)}
					</div>
				)}
				<div className="space-y-space-16 mt-space-16 max-w-125">
					<TransferInput
						accountId={accountUuid}
						isDisabled={isDisabled}
						isoCodes={isoCodes}
						isReinit={!!operation}
					/>
					{hasBoth && <PaymentTypeSwitcher disabled={isDisabled} />}
					<FormBuilder disabled={isDisabled} fields={allFields} />
					<Controller
						control={fxTransferFrom.control}
						name="payment_details"
						render={({ field }) => (
							<TextArea
								id="paymentDescription"
								{...field}
								className="mt-space-24"
								error={fxTransferFrom.formState.errors?.payment_details?.message}
								isDisabled={isDisabled}
								isRequired
								label={t('payments.newPayment.data.transferType.description')}
							/>
						)}
						rules={{
							required: t('payments.newPayment.fieldIsRequired'),
							maxLength: {
								message: t('payments.common.maxLength', { maxLength: 140 }),
								value: 140,
							},
						}}
					/>
					<Controller
						control={fxTransferFrom.control}
						name="execution_date"
						render={({ field: { ref: _ref, value, ...rest } }) => (
							<DateStringPicker
								{...rest}
								className="mt-space-16"
								disabledPast
								endMonth={addYears(startOfDay(new Date()), 1)}
								error={fxTransferFrom.formState.errors.execution_date?.message}
								isDisabled={isDisabled}
								label={t('payments.newPayment.data.date')}
								locale={i18n.language === 'lt' ? lt : enUS}
								value={value as string}
							/>
						)}
						rules={{ required: t('payments.newPayment.fieldIsRequired') }}
					/>
					<DocumentNumberInput
						accountId={accountUuid}
						disabled={formStatus === FormStatus.CREATED}
					/>
				</div>
				{formStatus === FormStatus.INITIAL && (
					<Button
						className="mt-6"
						disabled={isPending || !fxQuote || fxTransferFrom?.formState?.isValidating}
						type="submit"
						variant="primary"
					>
						{t('payments.common.createButton')}
					</Button>
				)}
				{failedToCreateTransaction ? (
					<Alert className="mt-space-16" type="error">
						{t('payments.common.orderFailed')}
					</Alert>
				) : (
					formStatus === FormStatus.CREATED && (
						<Alert className="mt-space-16" type="success">
							{t('payments.common.orderCreated')}
						</Alert>
					)
				)}
				{formStatus === FormStatus.CREATED && (
					<PermissionGate capability={Capability.SIGN_PAYMENT}>
						<div style={{ marginTop: '14px' }}>
							<PaymentTfa
								onSign={handleSignTransaction}
								transactionId={createdTransaction?.id}
							/>
						</div>
					</PermissionGate>
				)}
			</form>
			<FormBlocker
				hasChanges={
					Object.keys(fxTransferFrom.formState.dirtyFields).length > 0 &&
					formStatus !== FormStatus.CREATED
				}
			/>
		</FormProvider>
	);
};
